import { isRecognizedPerplexityHost } from '#pplx/frontend/auth/utils/resolveSameSiteRedirect.js';
// Only ever redirect AWAY FROM a production consumer origin. This is an
// explicit allowlist on purpose — NOT `env.isProduction`, which classifies
// `staging.perplexity.ai` as production and is what previously caused staging
// users to be funneled to the prod enterprise host.
const PROD_CONSUMER_ORIGINS = new Set(['https://www.perplexity.ai', 'https://www.perplexity.com']);
/**
 * Whether home-host routing can ever redirect away from `origin`. Lets bootstrap
 * skip waiting for the session everywhere else (enterprise, staging, testing,
 * previews, localhost).
 */
export const canRedirectToHomeHostFrom = (origin) => PROD_CONSUMER_ORIGINS.has(origin);
// Never bounce auth flows, API calls, or the host-scoped MS Office add-in.
const EXCLUDED_PREFIXES = ['/auth/', '/api/', '/ms-office-addin/'];
/**
 * Target URL to send a logged-in user to their account's home origin
 * (`home_host` from the session), preserving path + search; `null` when no
 * redirect should happen.
 *
 * Returns `null` when: `homeHost` is absent/unparseable, the current origin is
 * not a production consumer origin (so staging/console/preview/localhost never
 * redirect), the user is already on their home origin, the home host isn't a
 * recognized Perplexity host, or the path is excluded. The result only ever
 * points at the recognized Perplexity home origin, so it can't be an open
 * redirect. `currentOrigin` is passed in so this stays a pure, testable
 * function.
 */
export const resolveHomeHostRedirect = ({ homeHost, currentOrigin, pathname, search, }) => {
    if (!homeHost) {
        return null;
    }
    if (!canRedirectToHomeHostFrom(currentOrigin)) {
        return null;
    }
    let home;
    try {
        home = new URL(homeHost);
    }
    catch {
        return null;
    }
    if (home.origin === currentOrigin) {
        return null;
    }
    if (!isRecognizedPerplexityHost(home.hostname)) {
        return null;
    }
    if (EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
        return null;
    }
    return home.origin + pathname + search;
};
//# sourceMappingURL=resolveHomeHostRedirect.js.map